// Canonical 3-state theme: system | light | dark (per UI_PATTERNS canon §3.7.6)
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const ThemeCtx = createContext(null);
const STORAGE_KEY = "mp-roadmap.theme";

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "system";
    } catch {
      return "system";
    }
  });

  // cycle-5c+25-fix39 (Code Quality Triage): track media-prefers-dark as the
  // only state-input from the system, then DERIVE `effective` synchronously
  // from `theme` + `mediaPrefersDark`. This eliminates the previous
  // setState-in-effect pattern (lint rule react-hooks/set-state-in-effect)
  // where `effective` was a separate useState that the theme-change effect
  // had to setEffective() on every theme switch.
  const [mediaPrefersDark, setMediaPrefersDark] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const effective = theme === "system" ? (mediaPrefersDark ? "dark" : "light") : theme;

  // Apply effective theme to DOM + persist preference to localStorage.
  useEffect(() => {
    if (effective === "dark") document.documentElement.setAttribute("data-theme", "dark");
    else document.documentElement.removeAttribute("data-theme");
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      // localStorage may be blocked
    }
  }, [effective, theme]);

  // Subscribe to system prefers-color-scheme changes; updates
  // `mediaPrefersDark` only — the derived `effective` recomputes
  // synchronously above. Listener is always-on (cheap); when `theme !==
  // "system"` the media value is simply ignored by the derived expression.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setMediaPrefersDark(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const cycle = useCallback(() => {
    setTheme((cur) => {
      const order = ["system", "light", "dark"];
      const idx = order.indexOf(cur);
      return order[(idx + 1) % order.length];
    });
  }, []);

  // cycle-5c+25-fix39 (Code Quality Triage): memoise the context value so
  // useTheme() consumers don't re-render on every ThemeProvider re-render.
  // `setTheme` from useState is stable; `cycle` is stable via useCallback;
  // `theme` + `effective` are the actual change drivers.
  const value = useMemo(
    () => ({ theme, effective, cycle, setTheme }),
    [theme, effective, cycle],
  );

  return (
    <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>
  );
}

export function useTheme() {
  const v = useContext(ThemeCtx);
  if (!v) throw new Error("useTheme must be used within ThemeProvider");
  return v;
}
